/**
 * OAuth SSO Authentication Service
 * Handles OAuth authorization flow (frontend) and minimal token storage
 * Backend handles token validation, refresh, and secure storage
 */

import { AuthConfig } from '../types/api/AuthConfig';
import { storeOAuthReturnPath } from '../utils/oauthReturnPath';

export class AuthService {
  private config: AuthConfig | null = null;

  /**
   * Initialize auth service with configuration
   */
  initialize(config: AuthConfig): void {
    this.config = config;
  }

  /**
   * Initiate OAuth login flow with full page redirect
   */
  login(): void {
    if (!this.config) {
      throw new Error('Auth service not initialized. Please configure OAuth settings.');
    }

    storeOAuthReturnPath();

    // Use studentId from the current URL as OAuth state so it is available later in the callback.
    const studentIdFromUrl = this.getStudentIdFromCurrentUrl();
    const stateValue = studentIdFromUrl || this.generateState();

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scope || 'openid profile email',
      state: stateValue,
    });

    // Store state (studentId or random) for potential CSRF protection / later use
    sessionStorage.setItem('oauth_state', stateValue);

    const authUrl = `${this.config.authorizationEndpoint}?${params.toString()}`;
    window.location.href = authUrl;
  }

  /**
   * Logout user
   */
  logout(): void {
    if (typeof window === 'undefined') return;

    // Inform backend to clear HttpOnly auth cookies, then return to root.
    fetch('/auth/logout', {
      method: 'POST',
      credentials: 'include',
    }).finally(() => {
      window.location.href = '/';
    });
  }

  /**
   * Read studentId from the current URL query string, if present.
   * This value is used as the OAuth state so it can be recovered on callback.
   */
  private getStudentIdFromCurrentUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('studentId');
  }

  /**
   * Generate random state for CSRF protection when no studentId is available.
   */
  private generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

}

export const authService = new AuthService();
